import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FieldType, FormField } from '@shared/shared/form-fields';
import { NotifierService } from '@shared/core';
import { FormFieldsInstanceService } from '../../../services/form-fields-instance.service';
import { MailReplyService } from '../services/mail-reply.service';
import { MailMessage } from '../types';
import { getErrorString } from '@shared/core/utils';
import { registerTranslationsManually } from '@skyeng/libs/shared/i18n';
import { TranslocoService } from '@ngneat/transloco';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

interface ReplyForm {
  subject: FormField;
  body: FormField;
}

@DecorateUntilDestroy()
@Component({
  selector: 'crm-mail-reply',
  templateUrl: './mail-reply.component.html',
  styleUrls: ['./mail-reply.component.scss'],
  providers: [FormFieldsInstanceService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailReplyComponent implements OnInit, OnDestroy {
  @Input() messageToReply: MailMessage;

  @Output() sent = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  form: UntypedFormGroup;

  fieldsMap: ReplyForm;

  constructor(
    private formFields: FormFieldsInstanceService,
    private mailReply: MailReplyService,
    private notifier: NotifierService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.registerTranslations();
    this.setupControls();
    this.initForm();
    this.fillForm();
  }

  ngOnDestroy(): void {}

  onSubmit(): void {
    const formValues = this.formFields.serializeValues();

    this.mailReply
      .reply(this.messageToReply.id, {
        subject: String(formValues.subject),
        body: String(formValues.body),
      })
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        isSent => {
          if (isSent) {
            this.notifier.show(this.translocoService.translate('mailReply.messageSent'));
            this.sent.emit();
          }
        },
        error =>
          this.notifier.error(
            this.translocoService.translate('mailReply.failedToSendMessage', { error: getErrorString(error) }),
          ),
      );
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private registerTranslations(): void {
    registerTranslationsManually('mailReply', this.translocoService, import.meta.webpackContext!('./translation'));
  }

  private setupControls(): void {
    this.fieldsMap = {
      subject: {
        key: 'subject',
        type: FieldType.text,
        name: this.translocoService.translate('mailReply.subject'),
        validators: {
          required: true,
        },
      },
      body: {
        key: 'body',
        type: FieldType.textarea,
        name: this.translocoService.translate('mailReply.text'),
        validators: {
          required: true,
        },
      },
    };
  }

  private initForm(): void {
    this.form = this.formFields.getForm();
  }

  private fillForm(): void {
    this.fieldsMap.subject = {
      ...this.fieldsMap.subject,
      value: this.messageToReply.subject,
    };

    this.formFields.setFields(Object.values(this.fieldsMap));
  }
}
